import type { Scope } from '@shiocast/contracts';

export interface NavItem {
  /** A message key, resolved at render time so the rail follows the chosen language. */
  label: string;
  to: string;
  icon: string;
  scope?: Scope;
}

export interface NavGroup {
  /** A message key. */
  label: string;
  items: NavItem[];
}

/**
 * The navigation, in the masterplan's order (section 21). ON AIR comes first because
 * that is the operational priority, not because it is alphabetically convenient.
 *
 * The scope on an item is the one needed to see the page at all; the router's route meta
 * carries the same value so the rail and the guard never disagree.
 */
export const NAVIGATION: NavGroup[] = [
  {
    label: 'nav.onAir',
    items: [
      { label: 'nav.dashboard', to: '/', icon: 'Radio', scope: 'playout:view' },
      {
        label: 'nav.timeline',
        to: '/on-air/timeline',
        icon: 'ListMusic',
        scope: 'playout:view',
      },
      {
        label: 'nav.requests',
        to: '/on-air/requests',
        icon: 'MessageSquare',
        scope: 'playout:view',
      },
      {
        label: 'nav.liveStudio',
        to: '/on-air/live',
        icon: 'Mic',
        scope: 'live:manage_streamers',
      },
    ],
  },
  {
    label: 'nav.programming',
    items: [
      {
        label: 'nav.schedule',
        to: '/programming/schedule',
        icon: 'CalendarDays',
        scope: 'schedule:view',
      },
      {
        label: 'nav.clocks',
        to: '/programming/clocks',
        icon: 'Clock',
        scope: 'schedule:view',
      },
      {
        label: 'nav.shows',
        to: '/programming/shows',
        icon: 'Mic2',
        scope: 'schedule:view',
      },
      {
        label: 'nav.episodes',
        to: '/programming/episodes',
        icon: 'FileAudio',
        scope: 'schedule:view',
      },
      {
        label: 'nav.playlists',
        to: '/programming/playlists',
        icon: 'ListVideo',
        scope: 'schedule:view',
      },
      {
        label: 'nav.playlistStudio',
        to: '/programming/playlist-studio',
        icon: 'Sparkles',
        scope: 'schedule:view',
      },
    ],
  },
  {
    label: 'nav.media',
    items: [
      {
        label: 'nav.library',
        to: '/media/library',
        icon: 'Library',
        scope: 'media:view',
      },
      {
        label: 'nav.uploads',
        to: '/media/uploads',
        icon: 'Upload',
        scope: 'media:manage',
      },
      {
        label: 'nav.recordings',
        to: '/media/recordings',
        icon: 'Disc3',
        scope: 'media:view',
      },
      {
        label: 'nav.archive',
        to: '/media/archive',
        icon: 'Archive',
        scope: 'media:view',
      },
    ],
  },
  {
    label: 'nav.podcasts',
    items: [
      {
        label: 'nav.shows',
        to: '/podcasts/shows',
        icon: 'Podcast',
        scope: 'podcast:view',
      },
      {
        label: 'nav.distribution',
        to: '/podcasts/distribution',
        icon: 'Rss',
        scope: 'podcast:manage',
      },
      {
        label: 'nav.analytics',
        to: '/podcasts/analytics',
        icon: 'ChartLine',
        scope: 'analytics:view',
      },
    ],
  },
  {
    label: 'nav.audience',
    items: [
      {
        label: 'nav.liveListeners',
        to: '/audience/live',
        icon: 'Users',
        scope: 'analytics:view',
      },
      {
        label: 'nav.analytics',
        to: '/audience/analytics',
        icon: 'ChartColumn',
        scope: 'analytics:view',
      },
      {
        label: 'nav.streamHealth',
        to: '/audience/stream-health',
        icon: 'Activity',
        scope: 'station:view',
      },
    ],
  },
  {
    label: 'nav.operations',
    items: [
      {
        label: 'nav.systemHealth',
        to: '/operations/health',
        icon: 'HeartPulse',
        scope: 'station:view',
      },
      {
        label: 'nav.alerts',
        to: '/operations/alerts',
        icon: 'BellRing',
        scope: 'station:view',
      },
      {
        label: 'nav.jobs',
        to: '/operations/jobs',
        icon: 'ListChecks',
        scope: 'station:view',
      },
      {
        label: 'nav.auditLog',
        to: '/operations/audit',
        icon: 'ScrollText',
        scope: 'audit:view',
      },
    ],
  },
  {
    label: 'nav.station',
    items: [
      {
        label: 'nav.streams',
        to: '/station/streams',
        icon: 'AudioLines',
        scope: 'station:view',
      },
      {
        label: 'nav.domains',
        to: '/station/domains',
        icon: 'Globe',
        scope: 'station:view',
      },
      {
        label: 'nav.streamers',
        to: '/station/streamers',
        icon: 'UserRound',
        scope: 'live:manage_streamers',
      },
      {
        label: 'nav.fallback',
        to: '/station/fallback',
        icon: 'ShieldAlert',
        scope: 'station:configure',
      },
      {
        label: 'nav.users',
        to: '/station/users',
        icon: 'UsersRound',
        scope: 'users:manage',
      },
      {
        label: 'nav.notifications',
        to: '/station/notifications',
        icon: 'Phone',
        scope: 'station:view',
      },
      {
        label: 'nav.apiKeys',
        to: '/station/api-keys',
        icon: 'KeyRound',
        scope: 'station:view',
      },
      {
        label: 'nav.settings',
        to: '/station/settings',
        icon: 'Settings',
        scope: 'settings:manage',
      },
    ],
  },
];
